import clsx from 'clsx';
import { addNotification } from 'Components/Notifications/actions';
import { Tip } from 'grommet';
import { LoaderCircleIcon, MinusIcon, PlusIcon } from 'lucide-preact';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';
import { GlobalsContext } from 'Shared/context';
import { buildURL, get } from 'Shared/fetch';
import { trackMP } from 'Shared/mp';

import { TipContent } from '@/components/Tooltip';
import TourGlowRing from '@/components/Tours/components/TourGlowRing';
import { getConfig, getDesktopOrMobile } from '@/helpers';
import { toggleStreamWarningMsg } from '@/redux/actions';
import { useSelector } from '@/redux/helper';
import { selectCanAccessCurrentHabitat, selectCanControlCameraHere } from '@/redux/selectors/users';
import { setUserData } from '@/redux/slices/user/actions';
import appEvents from '@/shared/appEvents';

import style from './style.scss';

const initialZoomState = {
  currentZoom: null,
  requestedZoom: null,
};

const ZOOM_LEVELS = [100, 80, 60, 40, 20, 0];

const TooltipWrapper = ({ show, tooltip, children, horizontal }) => {
  if (!show) {
    return children;
  }

  return (
    <Tip dropProps={{ align: horizontal ? { bottom: 'top' } : { left: 'right' } }} content={tooltip} plain>
      {children}
    </Tip>
  );
};

const ZoomBar = ({ horizontal, width = '100%', height = '30px', className }) => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.user?.userId);
  const bannedUntil = useSelector((state) => state.user?.bannedUntil);
  const habitatId = useSelector((state) => state.habitat?.habitatInfo?._id);
  const configs = useSelector((state) => state.habitat?.habitatInfo?.selectedCamera?.configs);
  const cameraId = useSelector((state) => state.habitat?.habitatInfo?.selectedCamera?._id);
  const hostOnly = useSelector((state) => state.habitat?.habitatInfo?.hostOnly);
  const membersOnly = useSelector((state) => state.habitat?.habitatInfo?.membersOnly || {});
  const { socket } = useContext(GlobalsContext);
  const [zoomInfo, setZoomInfo] = useState(initialZoomState);
  const [isLoading, setIsLoading] = useState(false);
  const wrapperRef = useRef();
  const device = getDesktopOrMobile();

  const isCreatingUGC = useSelector((state) => state.mainStream.isCreatingUGC);

  const zoomTooltip = (message) => (isCreatingUGC ? 'Generating post...' : message);

  const onPropagateZoom = useCallback(
    ({ zoom }) => {
      setZoomInfo({ ...zoomInfo, currentZoom: zoom, requestedZoom: null });
      if (isLoading) {
        setIsLoading(false);
      }
    },
    [setZoomInfo, isLoading, zoomInfo],
  );

  const onZoomBlocked = ({ message, userId: id }) => {
    if (message && userId === id) {
      dispatch(toggleStreamWarningMsg(message));

      get(buildURL('/users/user'))
        .then((user) => dispatch(setUserData(user)))
        .catch((error) => console.error('Failed to re-fetch user object', error));

      setTimeout(() => dispatch(toggleStreamWarningMsg(false)), 5000);
    }
  };

  useEffect(() => {
    if (socket) {
      socket.on('propagateZoom', onPropagateZoom);
      socket.on('userSetZoomBlocked', onZoomBlocked);
    }
    return () => {
      if (socket) {
        socket.off('propagateZoom', onPropagateZoom);
        socket.off('userSetZoomBlocked', onZoomBlocked);
      }
    };
  }, [socket, onPropagateZoom]);

  // The two camera modes, as the spec states them: host-only lets through whoever holds
  // `habitat.cameraMode` here, members-only whoever holds `habitat.access` here.
  const canControlCamera = useSelector(selectCanControlCameraHere);
  const canAccessHabitat = useSelector(selectCanAccessCurrentHabitat);
  const clickBlockedByHostMode = hostOnly?.enabled && !canControlCamera;
  const clickBlockedByMembersOnlyMode = useMemo(() => {
    const { enabled, validUntil } = membersOnly;
    return enabled && new Date(validUntil) > new Date() && !canAccessHabitat;
  }, [membersOnly, canAccessHabitat]);

  const onClick = (requestedZoom, currentZoom) => {
    if (isCreatingUGC) {
      return;
    }

    if (bannedUntil && new Date(bannedUntil) > new Date()) {
      dispatch(
        addNotification({
          id: `banned-${Math.random()}`,
          type: 'toast',
          text: `You have been timed out and cannot use zoom controls until ${new Date(bannedUntil).toLocaleTimeString()}`,
          timeout: 6000,
        }),
      );

      return;
    }

    if (requestedZoom === currentZoom) {
      return;
    }

    if (clickBlockedByMembersOnlyMode) {
      dispatch(toggleStreamWarningMsg('Camera controls are currently disabled for free users'));
      setTimeout(() => dispatch(toggleStreamWarningMsg(false)), 5000);
      return;
    }

    if (clickBlockedByHostMode) {
      dispatch(toggleStreamWarningMsg('Camera is currently being controlled by host'));
      setTimeout(() => dispatch(toggleStreamWarningMsg(false)), 5000);
      return;
    }

    setIsLoading(true);

    socket.emit('userSetZoom', {
      zoom: requestedZoom,
      userId,
      habitatId,
      cameraId,
    });
    setZoomInfo({ currentZoom, isLoading: true, requestedZoom });
    setTimeout(() => setIsLoading(false), getConfig(configs, 'ptu.votingTime')?.configValue);
    trackMP('zoom', {
      habitatId,
      cameraId,
      currentZoom,
      requestedZoom,
      zoomType: requestedZoom > currentZoom ? 'in' : 'out',
    });
    // Emitted here rather than on the inbound `propagateZoom`, which also fires for other
    // viewers in the habitat — a tour step must only advance on what THIS user did.
    appEvents.emit('habitat:camera-zoomed', { habitatId, cameraId });
  };

  return (
    <div ref={wrapperRef} data-tour-target="zoom" className={clsx(style.zoomContainer, className)} style={{ width, height }}>
      {/* Ring portaled to <body> so the stream/control overflow can't clip its glow halo. The
          measured element is the pill itself (border-box), so zero padding hugs its edge. */}
      <TourGlowRing target="zoom" anchorRef={wrapperRef} padding={0} />
      <div
        className={clsx(style.zoomWrapper, {
          '!flex-row-reverse': horizontal,
        })}
      >
        <TooltipWrapper
          show={device !== 'mobile'}
          tooltip={<TipContent message={zoomTooltip('Zoom In')} />}
          horizontal={horizontal}
        >
          <button
            type="button"
            disabled={isCreatingUGC || isLoading || zoomInfo.currentZoom === 100}
            className={clsx(
              'preflight preflight-button items-center',
              style.iconWrapper,
              (isCreatingUGC || isLoading || zoomInfo.currentZoom === 100) && style.disabled,
              horizontal && '!h-full !w-auto !min-w-fit',
            )}
            onClick={() => onClick(zoomInfo.currentZoom + 20, zoomInfo.currentZoom)}
          >
            <PlusIcon className="size-[1em]" />
          </button>
        </TooltipWrapper>
        <div className={clsx(style.levels, { '!flex-grow !flex-row-reverse': horizontal })}>
          {ZOOM_LEVELS.map((zLvl) => (
            <TooltipWrapper
              key={zLvl}
              show={device !== 'mobile'}
              tooltip={<TipContent message={zoomTooltip('Zoom Controls')} />}
              horizontal={horizontal}
            >
              <div
                className={clsx(style.dotWrapper, {
                  // eslint-disable-next-line max-len
                  [style.active]: zoomInfo.currentZoom === zLvl || zoomInfo.requestedZoom === zLvl,
                  [style.disabled]: isCreatingUGC || (isLoading && zoomInfo.requestedZoom !== zLvl),
                })}
                onClick={() => onClick(zLvl, zoomInfo.currentZoom)}
              >
                <span key={zLvl} className={style.dot} />
                {zoomInfo.requestedZoom === zLvl && isLoading && (
                  <div className={style.loader}>
                    <LoaderCircleIcon className="size-[1em] animate-spin" />
                  </div>
                )}
              </div>
            </TooltipWrapper>
          ))}
        </div>
        <TooltipWrapper
          show={device !== 'mobile'}
          tooltip={<TipContent message={zoomTooltip('Zoom Out')} />}
          horizontal={horizontal}
        >
          <button
            type="button"
            disabled={isCreatingUGC || zoomInfo.currentZoom === 0 || isLoading}
            className={clsx(
              'preflight preflight-button items-center',
              style.iconWrapper,
              (isCreatingUGC || zoomInfo.currentZoom === 0 || isLoading) && style.disabled,
              horizontal && '!h-full !w-auto !min-w-fit',
            )}
            onClick={() => onClick(zoomInfo.currentZoom - 20, zoomInfo.currentZoom)}
          >
            <MinusIcon className="size-[1em]" />
          </button>
        </TooltipWrapper>
      </div>
    </div>
  );
};

export default ZoomBar;
